import { useEffect, useRef, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSnow, CloudDrizzle, CloudLightning, CloudFog, Cloudy } from "lucide-react";

interface WeatherData {
  current: {
    temp: number;
    feels: number;
    summary: string;
    icon: number;
    pop: number;
    wind: number;
  };
  hourly: Array<{
    time: string;
    temp: number;
    pop: number;
    icon: number;
  }>;
  daily: Array<{
    day: string;
    high: number;
    low: number;
    icon: number;
  }>;
  alerts: Array<{
    type: 'info' | 'warn' | 'danger';
    title: string;
    text: string;
  }>;
}

const WeatherModule = () => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [location, setLocation] = useState("San Antonio, TX");
  const [updated, setUpdated] = useState("");
  const [radarExpanded, setRadarExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());
  const [isRadarRefreshing, setIsRadarRefreshing] = useState(false);
  const [isForecastRefreshing, setIsForecastRefreshing] = useState(false);
  const [hourlyLive, setHourlyLive] = useState(true);
  const [dailyLive, setDailyLive] = useState(true);
  const [radarLive, setRadarLive] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const radarRef = useRef<HTMLIFrameElement>(null);
  const hourlyIntervalRef = useRef<number | null>(null);
  const dailyIntervalRef = useRef<number | null>(null);
  const radarIntervalRef = useRef<number | null>(null);

  const fetchWeather = async (lat = 29.4241, lon = -98.4936, locName = "San Antonio, TX") => {
    try {
      setIsForecastRefreshing(true);
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,apparent_temperature,precipitation_probability,wind_speed_10m,weather_code&hourly=temperature_2m,precipitation_probability,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch&timezone=America%2FChicago&forecast_days=7`;
      const res = await fetch(url);
      const data = await res.json();
      
      const current = data.current;
      const hourly = data.hourly;
      const daily = data.daily;

      // Build weather data
      const weatherData: WeatherData = {
        current: {
          temp: Math.round(current.temperature_2m),
          feels: Math.round(current.apparent_temperature),
          summary: getWeatherSummary(current.weather_code),
          icon: current.weather_code,
          pop: current.precipitation_probability || 0,
          wind: Math.round(current.wind_speed_10m)
        },
        hourly: hourly.time.slice(0, 24).map((time: string, i: number) => ({
          time: new Date(time).toLocaleTimeString('en-US', { hour: 'numeric' }),
          temp: Math.round(hourly.temperature_2m[i]),
          pop: hourly.precipitation_probability[i] || 0,
          icon: hourly.weather_code[i]
        })),
        daily: daily.time.map((day: string, i: number) => ({
          day: new Date(day).toLocaleDateString('en-US', { weekday: 'short' }),
          high: Math.round(daily.temperature_2m_max[i]),
          low: Math.round(daily.temperature_2m_min[i]),
          icon: daily.weather_code[i]
        })),
        alerts: generateAlerts(current, hourly)
      };

      setWeather(weatherData);
      setLocation(locName);
      setUpdated(new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
      
      // Hide refreshing status after 2 seconds
      setTimeout(() => {
        setIsForecastRefreshing(false);
      }, 2000);
    } catch (err) {
      console.error('Weather fetch error:', err);
      setIsForecastRefreshing(false);
    }
  };

  const getWeatherSummary = (code: number): string => {
    const summaries: { [key: number]: string } = {
      0: 'Clear', 1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
      45: 'Foggy', 48: 'Foggy', 51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
      61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain', 71: 'Light Snow', 73: 'Snow',
      75: 'Heavy Snow', 80: 'Rain Showers', 81: 'Rain Showers', 82: 'Heavy Rain Showers',
      95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Severe Thunderstorm'
    };
    return summaries[code] || 'Unknown';
  };

  const getWeatherIcon = (code: number) => {
    const iconProps = { size: 40, strokeWidth: 1.5 };
    
    if (code === 0) return <Sun {...iconProps} className="text-[#e76f51]" />;
    if ([1, 2].includes(code)) return <Cloudy {...iconProps} className="text-[#b3b3b3]" />;
    if (code === 3) return <Cloud {...iconProps} className="text-[#999999]" />;
    if ([45, 48].includes(code)) return <CloudFog {...iconProps} className="text-[#b3b3b3]" />;
    if ([51, 53, 55].includes(code)) return <CloudDrizzle {...iconProps} className="text-[#4a9eff]" />;
    if ([61, 63, 65, 80, 81, 82].includes(code)) return <CloudRain {...iconProps} className="text-[#2d7dd2]" />;
    if ([71, 73, 75].includes(code)) return <CloudSnow {...iconProps} className="text-[#b3d4ff]" />;
    if ([95, 96, 99].includes(code)) return <CloudLightning {...iconProps} className="text-[#e76f51]" />;
    
    return <Cloud {...iconProps} className="text-[#b3b3b3]" />;
  };

  const generateAlerts = (current: any, hourly: any): WeatherData['alerts'] => {
    const alerts: WeatherData['alerts'] = [];
    
    // Heavy rain alert
    const heavyRainHours = hourly.precipitation_probability.slice(0, 12).filter((p: number) => p > 70).length;
    if (heavyRainHours >= 3) {
      alerts.push({
        type: 'danger',
        title: 'Heavy Rain Expected',
        text: `${heavyRainHours} hours of heavy rain in next 12h. Plan extra travel time.`
      });
    }

    // Wind alert
    if (current.wind_speed_10m > 25) {
      alerts.push({
        type: 'warn',
        title: 'High Winds',
        text: `Wind speeds of ${Math.round(current.wind_speed_10m)} mph. Drive carefully.`
      });
    }

    // Temperature extremes
    if (current.temperature_2m > 95) {
      alerts.push({
        type: 'warn',
        title: 'Extreme Heat',
        text: 'Stay hydrated. Vehicle cooling running 10min before pickup.'
      });
    } else if (current.temperature_2m < 35) {
      alerts.push({
        type: 'warn',
        title: 'Cold Weather',
        text: 'Vehicle heating running 10min before pickup.'
      });
    }

    // Thunderstorm
    if (current.weather_code >= 95) {
      alerts.push({
        type: 'danger',
        title: 'Thunderstorm Active',
        text: 'Severe weather detected. Monitor conditions closely.'
      });
    }

    return alerts;
  };

  const useCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          try {
            const geoRes = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
            const geoData = await geoRes.json();
            const cityName = geoData.address.city || geoData.address.town || geoData.address.village || 'Your Location';
            fetchWeather(latitude, longitude, `${cityName}, ${geoData.address.state || ''}`);
          } catch {
            fetchWeather(latitude, longitude, 'Your Location');
          }
        },
        () => {
          alert('Location access denied. Using San Antonio, TX.');
          fetchWeather();
        }
      );
    }
  };

  const toggleRadar = () => {
    setRadarExpanded(!radarExpanded);
    if (!radarExpanded && radarRef.current && !radarRef.current.src) {
      radarRef.current.src = radarRef.current.dataset.src || '';
    }
  };

  const refreshRadar = () => {
    if (radarRef.current) {
      setIsRadarRefreshing(true);
      const baseSrc = "https://embed.windy.com/embed2.html?lat=29.4241&lon=-98.4936&zoom=8&level=surface&overlay=radar&product=radar&marker=true&menu=false&message=false&autoplay=1&play=1&loop=1&calendar=off";
      const newSrc = `${baseSrc}&t=${Date.now()}`;
      radarRef.current.src = newSrc;
      setLastRefreshTime(Date.now());
      
      // Hide refreshing status after 2 seconds
      setTimeout(() => {
        setIsRadarRefreshing(false);
      }, 2000);
    }
  };

  // Toggle functions for Live buttons
  const toggleHourlyLive = () => {
    const newState = !hourlyLive;
    setHourlyLive(newState);
    if (newState) {
      fetchWeather(); // Immediate refresh when turned on
    }
  };

  const toggleDailyLive = () => {
    const newState = !dailyLive;
    setDailyLive(newState);
    if (newState) {
      fetchWeather(); // Immediate refresh when turned on
    }
  };

  const toggleRadarLive = () => {
    const newState = !radarLive;
    setRadarLive(newState);
    if (newState) {
      refreshRadar(); // Immediate refresh when turned on
    }
  };

  useEffect(() => {
    fetchWeather();
    
    // Intersection observer for scroll animation
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Independent auto-refresh for Hourly/Daily (every 5 minutes)
  useEffect(() => {
    if (hourlyLive || dailyLive) {
      if (hourlyIntervalRef.current) clearInterval(hourlyIntervalRef.current);
      hourlyIntervalRef.current = window.setInterval(() => {
        if (hourlyLive || dailyLive) {
          fetchWeather();
        }
      }, 5 * 60 * 1000);
    }

    return () => {
      if (hourlyIntervalRef.current) clearInterval(hourlyIntervalRef.current);
    };
  }, [hourlyLive, dailyLive]);

  // Independent auto-refresh for Radar (every 3 minutes)
  useEffect(() => {
    if (radarLive) {
      if (radarIntervalRef.current) clearInterval(radarIntervalRef.current);
      radarIntervalRef.current = window.setInterval(() => {
        if (radarLive) {
          refreshRadar();
        }
      }, 3 * 60 * 1000);
    }

    return () => {
      if (radarIntervalRef.current) clearInterval(radarIntervalRef.current);
    };
  }, [radarLive]);

  if (!weather) return <div style={{ height: '500px' }} />; // Placeholder to prevent CLS

  return (
    <div 
      ref={containerRef}
      className={`rw-container ${isVisible ? 'is-visible' : ''}`}
      style={{ minHeight: '500px' }}
      aria-live="polite"
    >
      <style>{`
        .rw-container { 
          font-family: 'Inter', system-ui, -apple-system, sans-serif; 
          color: #ffffff; 
          background: linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%); 
          border-radius: 12px; 
          padding: 20px; 
          max-width: 980px; 
          margin: 20px auto; 
          box-shadow: 
            0 8px 32px rgba(0,0,0,0.6),
            0 0 40px rgba(231, 111, 81, 0.6),
            0 0 80px rgba(231, 111, 81, 0.4),
            0 0 120px rgba(231, 111, 81, 0.2); 
          letter-spacing: 0.025em;
          opacity: 1;
          transform: none;
          transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          animation: rw-glow-pulse 2.5s ease-in-out infinite alternate;
          position: relative;
          z-index: 10;
          overflow: visible;
        }
        @keyframes rw-glow-pulse {
          0% { 
            box-shadow: 
              0 8px 32px rgba(0,0,0,0.6),
              0 0 40px rgba(231, 111, 81, 0.6),
              0 0 80px rgba(231, 111, 81, 0.4),
              0 0 120px rgba(231, 111, 81, 0.2);
          }
          100% { 
            box-shadow: 
              0 8px 32px rgba(0,0,0,0.6),
              0 0 60px rgba(231, 111, 81, 0.8),
              0 0 100px rgba(231, 111, 81, 0.6),
              0 0 140px rgba(231, 111, 81, 0.4);
          }
        }
        .rw-container.is-visible { 
          opacity: 1; 
          transform: none; 
        }
        .rw-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; flex-wrap: wrap; }
        .rw-title { font-weight: 700; letter-spacing: 0.05em; font-size: 1.1rem; color: #ffffff; }
        .rw-note { color: #b3b3b3; font-size: 0.85rem; opacity: 0.9; font-weight: 400; }
        .rw-controls { display: flex; gap: 8px; }
        .rw-btn { 
          background: #1a1a1a; 
          color: #ffffff; 
          border: 2px solid #e76f51; 
          padding: 8px 12px; 
          border-radius: 8px; 
          cursor: pointer; 
          transition: all 0.3s ease; 
          font-size: 0.85rem; 
          font-weight: 600;
        }
        .rw-btn:hover { 
          background: #e76f51;
          color: #ffffff; 
          transform: translateY(-1px) scale(1.02); 
          box-shadow: 0 4px 12px rgba(231, 111, 81, 0.3);
        }
        .rw-location-row { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin: 6px 0 12px; 
          color: #b3b3b3; 
          font-size: 0.95rem; 
          font-weight: 500;
        }
        .rw-updated { 
          font-size: 0.8rem; 
          opacity: 0.85; 
          color: #e76f51; 
          font-weight: 500;
          text-align: right;
        }
        .rw-current { 
          display: flex; 
          gap: 16px; 
          align-items: center; 
          background: linear-gradient(135deg, rgba(231, 111, 81, 0.15) 0%, rgba(231, 111, 81, 0.05) 100%); 
          border: 2px solid #e76f51; 
          padding: 20px; 
          border-radius: 12px; 
          box-shadow: 0 4px 16px rgba(231, 111, 81, 0.25);
        }
        .rw-current-icon svg { width: 64px; height: 64px; }
        .rw-current-icon { display: flex; align-items: center; justify-content: center; }
        .rw-temp { 
          font-size: 2.8rem; 
          font-weight: 800; 
          color: #e76f51; 
          line-height: 1; 
          letter-spacing: -0.02em;
        }
        .rw-summary { font-size: 1.1rem; color: #ffffff; font-weight: 600; }
        .rw-details { color: #b3b3b3; display: flex; gap: 8px; align-items: center; font-size: 0.9rem; flex-wrap: wrap; font-weight: 500; }
        .rw-dot { opacity: 0.4; }
        .rw-section { margin-top: 24px; }
        .rw-section-title { 
          color: #e76f51; 
          font-weight: 700; 
          margin-bottom: 12px; 
          letter-spacing: 0.025em;
          font-size: 1.1rem;
        }
        .rw-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .rw-live-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(43, 214, 123, 0.15);
          color: #2bd67b;
          border: 2px solid rgba(43, 214, 123, 0.4);
          padding: 6px 14px;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.02em;
        }
        .rw-live-btn:hover {
          background: rgba(43, 214, 123, 0.25);
          border-color: rgba(43, 214, 123, 0.6);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(43, 214, 123, 0.3);
        }
        .rw-live-btn.inactive {
          background: rgba(179, 179, 179, 0.1);
          color: #b3b3b3;
          border-color: rgba(179, 179, 179, 0.3);
        }
        .rw-live-btn.inactive:hover {
          background: rgba(179, 179, 179, 0.15);
          border-color: rgba(179, 179, 179, 0.4);
          box-shadow: 0 4px 12px rgba(179, 179, 179, 0.2);
        }
        .rw-live-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #2bd67b;
          animation: rw-pulse-dot 2s ease-out infinite;
        }
        .rw-live-btn.inactive .rw-live-dot {
          background: #666666;
          animation: none;
        }
        @keyframes rw-pulse-dot {
          0% { box-shadow: 0 0 0 0 rgba(43, 214, 123, 0.7); }
          70% { box-shadow: 0 0 0 8px rgba(43, 214, 123, 0); }
          100% { box-shadow: 0 0 0 0 rgba(43, 214, 123, 0); }
        }
        .rw-hourly { 
          display: flex; 
          gap: 10px; 
          overflow-x: auto; 
          padding-bottom: 8px; 
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
        }
        .rw-hour { 
          min-width: 74px; 
          background: #1a1a1a; 
          border: 2px solid #2a2a2a; 
          border-radius: 10px; 
          padding: 12px; 
          text-align: center; 
          scroll-snap-align: start; 
          transition: all 0.3s ease; 
          font-weight: 500;
          color: #ffffff;
        }
        .rw-hour .t { font-weight: 700; letter-spacing: -0.01em; color: #e76f51; }
        .rw-hour .p { color: #b3b3b3; font-size: 0.8rem; }
        .rw-hour svg { width: 32px; height: 32px; margin: 6px auto; }
        .rw-hour > div:nth-child(2) { display: flex; justify-content: center; }
        .rw-hour:hover { 
          transform: translateY(-2px) scale(1.02); 
          box-shadow: 0 6px 20px rgba(231, 111, 81, 0.35); 
          border-color: #e76f51;
          background: rgba(231, 111, 81, 0.1);
        }
        .rw-daily { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; }
        @media (max-width: 900px) { .rw-daily { grid-template-columns: repeat(4, 1fr); } }
        @media (max-width: 600px) { .rw-daily { grid-template-columns: repeat(2, 1fr); } }
        .rw-day { 
          background: #1a1a1a; 
          border: 2px solid #2a2a2a; 
          border-radius: 10px; 
          padding: 12px; 
          text-align: center; 
          transition: all 0.3s ease; 
          font-weight: 500;
          color: #ffffff;
        }
        .rw-day .hi { font-weight: 700; letter-spacing: -0.01em; color: #e76f51; }
        .rw-day .lo { color: #b3b3b3; font-size: 0.85rem; }
        .rw-day svg { width: 36px; height: 36px; margin: 6px auto; }
        .rw-day > div:nth-child(2) { display: flex; justify-content: center; }
        .rw-day:hover { 
          transform: translateY(-2px) scale(1.02); 
          box-shadow: 0 6px 20px rgba(231, 111, 81, 0.35); 
          border-color: #e76f51;
          background: rgba(231, 111, 81, 0.1);
        }
        .rw-footnote { 
          margin-top: 16px; 
          color: #b3b3b3; 
          font-size: 0.8rem; 
          opacity: 0.85; 
          font-weight: 400;
          letter-spacing: 0.02em;
          text-align: center;
        }
        .rw-alerts { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
        .rw-alert { 
          display: flex; 
          align-items: flex-start; 
          gap: 10px; 
          border-radius: 10px; 
          padding: 14px; 
          border: 2px solid transparent; 
          background: #1a1a1a;
          animation: rw-slide-in 0.4s ease both; 
          position: relative; 
          overflow: hidden; 
          font-weight: 500;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .rw-alert.info { border-color: #4a9eff; background: rgba(74, 158, 255, 0.1); }
        .rw-alert.warn { border-color: #f4a261; background: rgba(244, 162, 97, 0.1); }
        .rw-alert.danger { border-color: #e76f51; background: rgba(231, 111, 81, 0.1); }
        .rw-alert .pulse { 
          position: absolute; 
          width: 10px; 
          height: 10px; 
          border-radius: 50%; 
          top: 14px; 
          left: 14px; 
          background: currentColor; 
          color: inherit; 
        }
        .rw-alert.warn .pulse, .rw-alert.danger .pulse { animation: rw-pulse 2s ease-out infinite; }
        .rw-alert .msg { margin-left: 16px; }
        .rw-alert .title { font-weight: 600; letter-spacing: 0.01em; color: #ffffff; }
        .rw-alert .text { color: #b3b3b3; font-size: 0.9rem; }
        @keyframes rw-pulse { 
          0% { box-shadow: 0 0 0 0 rgba(244, 162, 97, 0.5); } 
          70% { box-shadow: 0 0 0 12px rgba(244, 162, 97, 0); } 
          100% { box-shadow: 0 0 0 0 rgba(244, 162, 97, 0); } 
        }
        @keyframes rw-slide-in { from { transform: translateY(8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .rw-radar-wrap iframe { width: 100%; height: 65vh; border: 0; border-radius: 10px; }
        .rw-radar-wrap.expanded iframe { height: 65vh !important; }
        .rw-radar-wrap:not(.expanded) iframe { height: 320px !important; }
        .rw-radar-status {
          font-size: 0.75rem;
          color: #2bd67b;
          font-weight: 600;
          padding: 4px 12px;
          border-radius: 6px;
          background: rgba(43, 214, 123, 0.1);
          border: 1px solid rgba(43, 214, 123, 0.3);
          transition: all 0.3s ease;
        }
        .rw-radar-status.refreshing {
          color: #e76f51;
          background: rgba(231, 111, 81, 0.1);
          border-color: rgba(231, 111, 81, 0.3);
          animation: rw-pulse-status 1s ease-in-out infinite;
        }
        @keyframes rw-pulse-status {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .rw-radar-toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; gap: 8px; }
        @media (prefers-reduced-motion: reduce) {
          .rw-container { transition: none !important; animation: none !important; }
          .rw-hour, .rw-day, .rw-btn, .rw-live-btn { transition: none !important; }
          .rw-alert { animation: none !important; }
          .rw-alert .pulse { animation: none !important; }
          .rw-live-dot { animation: none !important; }
        }
      `}</style>

      <div className="rw-header">
        <div className="rw-brand">
          <div className="rw-title">Rick's Black Car Service — Weather</div>
          <div className="rw-note">For accurate ride planning</div>
        </div>
        <div className="rw-controls">
          <button onClick={useCurrentLocation} className="rw-btn" title="Use my location">
            Use my location
          </button>
          <button onClick={() => { fetchWeather(); refreshRadar(); }} className="rw-btn" title="Refresh">
            Refresh
          </button>
        </div>
      </div>

      <div className="rw-location-row">
        <span id="rw-location">{location}</span>
      </div>
      
      {updated && (
        <div className="rw-updated">Last updated: {updated}</div>
      )}

      {weather.alerts.length > 0 && (
        <div className="rw-alerts">
          {weather.alerts.map((alert, i) => (
            <div key={i} className={`rw-alert ${alert.type}`}>
              <div className="pulse" />
              <div className="msg">
                <div className="title">{alert.title}</div>
                <div className="text">{alert.text}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rw-current">
        <div className="rw-current-icon">{getWeatherIcon(weather.current.icon)}</div>
        <div className="rw-current-main">
          <div className="rw-temp">{weather.current.temp}°</div>
          <div className="rw-summary">{weather.current.summary}</div>
          <div className="rw-details">
            <span>Feels {weather.current.feels}°</span>
            <span className="rw-dot">•</span>
            <span>Rain {weather.current.pop}%</span>
            <span className="rw-dot">•</span>
            <span>Wind {weather.current.wind} mph</span>
          </div>
        </div>
      </div>

      <div className="rw-section">
        <div className="rw-section-header">
          <div className="rw-section-title">Hourly</div>
          <button 
            onClick={toggleHourlyLive} 
            className={`rw-live-btn ${hourlyLive ? '' : 'inactive'}`}
            title={hourlyLive ? 'Auto-refresh enabled (every 5 min)' : 'Auto-refresh disabled'}
          >
            <span className="rw-live-dot"></span>
            Live Hourly
          </button>
        </div>
        <div className="rw-hourly">
          {weather.hourly.map((hour, i) => (
            <div key={i} className="rw-hour">
              <div>{hour.time}</div>
              <div>{getWeatherIcon(hour.icon)}</div>
              <div className="t">{hour.temp}°</div>
              <div className="p">{hour.pop}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rw-section">
        <div className="rw-section-header">
          <div className="rw-section-title">7‑Day</div>
          <button 
            onClick={toggleDailyLive} 
            className={`rw-live-btn ${dailyLive ? '' : 'inactive'}`}
            title={dailyLive ? 'Auto-refresh enabled (every 5 min)' : 'Auto-refresh disabled'}
          >
            <span className="rw-live-dot"></span>
            Live 7‑Day
          </button>
        </div>
        <div className="rw-daily">
          {weather.daily.map((day, i) => (
            <div key={i} className="rw-day">
              <div>{day.day}</div>
              <div>{getWeatherIcon(day.icon)}</div>
              <div className="hi">{day.high}°</div>
              <div className="lo">{day.low}°</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rw-section">
        <div className="rw-section-header">
          <div className="rw-section-title">Live Radar</div>
          <button 
            onClick={toggleRadarLive} 
            className={`rw-live-btn ${radarLive ? '' : 'inactive'}`}
            title={radarLive ? 'Auto-refresh enabled (every 3 min)' : 'Auto-refresh disabled'}
          >
            <span className="rw-live-dot"></span>
            Live Radar
          </button>
        </div>
        <div className={`rw-radar-wrap ${radarExpanded ? 'expanded' : ''}`}>
          <div className="rw-radar-toolbar">
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={toggleRadar} className="rw-btn" aria-expanded={radarExpanded}>
                {radarExpanded ? 'Collapse radar' : 'Expand radar'}
              </button>
              <button onClick={refreshRadar} className="rw-btn" title="Refresh radar">
                Refresh radar
              </button>
            </div>
          </div>
          <iframe
            ref={radarRef}
            title="Live Weather Radar"
            src="https://embed.windy.com/embed2.html?lat=29.4241&lon=-98.4936&zoom=8&level=surface&overlay=radar&product=radar&marker=true&menu=false&message=false&autoplay=1&play=1&loop=1&calendar=off"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            style={{ width: '100%', height: radarExpanded ? '65vh' : '320px', border: 0, borderRadius: '10px' }}
          />
        </div>
      </div>

      <div className="rw-footnote">Powered by Open‑Meteo</div>
    </div>
  );
};

export default WeatherModule;
